import {
  Component,
  signal,
  computed,
  OnInit,
  inject,
  OnDestroy,
  HostListener,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatDialogComponent } from '../chat-dialog/chat-dialog.component';
import { DifyGeneralService } from '../dify-general.service';
import { DifyVoiceReportService } from '../dify-voice-report.service';
import { AuthService } from '@/app/core/services/auth.service';
import { RoleEnum } from '@/app/consts/role.const';
import { environment } from '@/environments/environment';
import { lastValueFrom } from 'rxjs';
import { AssistantMode } from '../types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/** 悬浮球助手仅对这些用户邮箱开放（统一小写比较） */
const FAB_ALLOWED_EMAILS: string[] = [
  'vincent.zhou@scania.com.cn',
  'wesley.tan@scania.com.cn',
  'jingjing.zhang@scania.com.cn',
  'hyman.wang@scania.com.cn',
  'justin.wang@scania.com.cn',
  'madhu.cheluvaraju@scania.com.cn',
];

/** FAB Speed Dial 功能项 */
interface FabAction {
  id: AssistantMode;
  /** i18n key（label 与 description） */
  labelKey: string;
  descKey: string;
  /** 内联 SVG path（Tegel 无话筒图标，语音相关用内联 SVG 保证一致） */
  icon: string;
  /** 可选：仅指定角色可见（默认全部可见） */
  roles?: RoleEnum[];
}

@Component({
  selector: 'app-floating-ball',
  standalone: true,
  imports: [CommonModule, ChatDialogComponent, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './floating-ball.component.html',
  styleUrl: './floating-ball.component.scss',
})
export class FloatingBallComponent implements OnInit, OnDestroy {
  private readonly generalService = inject(DifyGeneralService);
  private readonly voiceReportService = inject(DifyVoiceReportService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);

  /** FAB 是否展开（Speed Dial 菜单） */
  fabOpen = signal(false);
  /** 当前打开的对话框模式（null = 未打开） */
  activeDialog = signal<AssistantMode | null>(null);
  isInitializing = signal(false);
  initError = signal<string | null>(null);

  /** FAB 功能项定义 */
  readonly actions: FabAction[] = [
    {
      id: 'voice-report',
      labelKey: 'floatingBall.createReport',
      icon: 'M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zM5 11a7 7 0 0 0 14 0M12 18v4', // 话筒
      descKey: 'floatingBall.createReportDesc',
      roles: [RoleEnum.Driver], // 仅 Driver 角色可见
    },
  ];

  /** 功能项 label 翻译 */
  protected actionLabel(action: FabAction): string {
    return this.translate.instant(action.labelKey);
  }

  /** 功能项 description 翻译 */
  protected actionDesc(action: FabAction): string {
    return this.translate.instant(action.descKey);
  }

  /** 当前角色是否为 Driver */
  protected isDriver = computed(() => this.authService.selectedRole() === RoleEnum.Driver);

  /** 按当前角色过滤后的可见功能项 */
  visibleActions = computed(() => {
    const role = this.authService.selectedRole() as RoleEnum;
    return this.actions.filter(a => !a.roles || a.roles.includes(role));
  });

  /** 是否显示 OTI/LP 快捷小圆 icon：仅 Driver 且已 check-in 车辆 */
  canQuickReport = computed(
    () => this.isDriver() && !!this.voiceReportService.canCreateReport()
  );

  /**
   * 悬浮球助手是否可见：
   * - 仅本地 & dev 环境（environmentName === 'development'）
   * - 仅指定用户邮箱
   */
  protected visible = computed(() => {
    if (environment.environmentName !== 'development') return false;
    const email = this.authService.getCurrentUserProfile()?.email?.toLowerCase() ?? '';
    return FAB_ALLOWED_EMAILS.includes(email) && this.visibleActions().length > 0;
  });

  // ================= 悬浮球拖动 =================

  /** 主按钮当前位置（px），默认右下角；拖动后可自由挪动，避免遮挡发送按钮等 */
  fabPos = signal<{ x: number; y: number }>(this.defaultFabPos());

  /** 计算初始位置：小屏（≤480px）按钮 52px+边距16，其余 60px+边距24 */
  private defaultFabPos(): { x: number; y: number } {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const small = window.innerWidth <= 480;
    const size = small ? 52 : 60;
    const margin = small ? 16 : 24;
    return {
      x: window.innerWidth - size - margin,
      y: window.innerHeight - size - margin,
    };
  }

  protected dragStart: { px: number; py: number; startX: number; startY: number } | null = null;
  protected suppressClick = false;

  /** 按下：记录起始点，捕获指针 */
  protected onFabPointerDown(event: PointerEvent): void {
    this.dragStart = {
      px: this.fabPos().x,
      py: this.fabPos().y,
      startX: event.clientX,
      startY: event.clientY,
    };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  /** 移动：更新位置（限制在可视区域内） */
  protected onFabPointerMove(event: PointerEvent): void {
    if (!this.dragStart) return;
    const { px, py, startX, startY } = this.dragStart;
    const size = 60; // 主按钮直径
    const maxX = typeof window !== 'undefined' ? window.innerWidth - size : px;
    const maxY = typeof window !== 'undefined' ? window.innerHeight - size : py;
    this.fabPos.update(() => ({
      x: Math.max(0, Math.min(maxX, px + (event.clientX - startX))),
      y: Math.max(0, Math.min(maxY, py + (event.clientY - startY))),
    }));
  }

  /** 抬起：结束拖动；若位移很小视为点击 */
  protected onFabPointerUp(event: PointerEvent): void {
    if (this.dragStart) {
      const moved =
        Math.abs(event.clientX - this.dragStart.startX) > 5 ||
        Math.abs(event.clientY - this.dragStart.startY) > 5;
      this.suppressClick = moved; // 拖动后抑制 toggleFab 的点击
    }
    this.dragStart = null;
  }

  /** 主按钮点击：若刚拖拽过则忽略，否则展开/收起 */
  protected onFabClick(): void {
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    this.toggleFab();
  }

  async ngOnInit(): Promise<void> {
    const profile = this.authService.getCurrentUserProfile();
    const role = this.authService.selectedRole();
    let token = '';
    try {
      token = (await lastValueFrom(this.authService.getAccessToken())) || '';
    } catch {
      token = localStorage.getItem('access_token') || '';
    }
    if (profile?.email) {
      this.generalService.setUser(profile.email, role || '', profile.displayName, token);
      this.voiceReportService.setUser(profile.email, role || '', profile.displayName, token);
    }
  }

  ngOnDestroy(): void {
    // 组件销毁时清理
  }

  /**
   * 点击 FAB 主按钮：展开/收起 Speed Dial 菜单。
   */
  protected toggleFab(event?: Event): void {
    event?.stopPropagation();
    // 若有打开的对话框，先关闭
    if (this.activeDialog()) {
      this.closeDialog();
      return;
    }
    this.fabOpen.update(v => !v);
  }

  /**
   * 点击功能项：打开对应对话框。
   */
  protected async onActionClick(action: FabAction, event?: Event): Promise<void> {
    event?.stopPropagation();
    const mode: AssistantMode = action.id;

    this.fabOpen.set(false);
    this.initError.set(null);

    // 只初始化当前要打开的模式对应的服务，避免一个服务禁用影响另一个
    const service = mode === 'general' ? this.generalService : this.voiceReportService;
    if (this.connectionIdle(service)) {
      this.isInitializing.set(true);
      try {
        await service.initialize();
      } catch (err: any) {
        this.initError.set(err.message || this.translate.instant('floatingBall.connectDifyFailed'));
      } finally {
        this.isInitializing.set(false);
      }
    }

    if (!this.initError()) {
      // 打开"创建差异报告"时，从 Dify 恢复最近一次未提交的报告会话（防误关丢失）
      if (mode === 'voice-report') {
        const user = this.voiceReportService.userEmail() || 'tvms-user';
        await this.voiceReportService.restoreLastDraft(user);
      }
      this.activeDialog.set(mode);
    }
  }

  /**
   * 点击 OTI/LP 快捷 icon：跳过选择类型，直接开始对应类型的报告采集。
   * @param type 报告类型（OTI / LP）
   */
  protected async openReportType(type: 'OTI' | 'LP', event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.canQuickReport()) return;

    this.fabOpen.set(false);
    this.initError.set(null);

    // 只初始化语音报报告（OTI/LP 快捷入口不受通用助手禁用影响）
    if (this.connectionIdle(this.voiceReportService)) {
      this.isInitializing.set(true);
      try {
        await this.voiceReportService.initialize();
      } catch (err: any) {
        this.initError.set(err.message || this.translate.instant('floatingBall.connectDifyFailed'));
      } finally {
        this.isInitializing.set(false);
      }
    }

    if (this.initError()) return;

    // 开始对应类型的报告并打开对话框（跳过 select-type）
    this.voiceReportService.startReportOfType(type);
    this.activeDialog.set('voice-report');
  }

  /**
   * 关闭对话框。
   */
  protected closeDialog(): void {
    this.activeDialog.set(null);
  }

  /**
   * 点击空白区域收起（FAB 菜单或对话框）。
   */
  protected onBackdropClick(event?: Event): void {
    event?.stopPropagation();
    this.fabOpen.set(false);
    this.activeDialog.set(null);
  }

  /**
   * 物理返回键（Android Back / Pad）收起。
   */
  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onBackdropClick();
    }
  }

  private connectionIdle(service: DifyGeneralService | DifyVoiceReportService): boolean {
    const s = service.connectionStatus();
    return s === 'idle' || s === 'error';
  }
}
